import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Box, Divider, Grid, Link, TextField, Typography } from '@mui/material';
import { signUp } from 'aws-amplify/auth';
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN_2 } from 'constants/paths';
import { Field, FieldProps, Form, Formik } from 'formik';

import { useAuthenticated } from 'hooks/useAuthenticated';
import usePromiseStatus from 'hooks/usePromiseStatus';

import { AuthModal, IStepFormProps as Props } from 'interfaces/auth';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { FormField } from 'components/_basic/Form/FormField';
import { PasswordTextField } from 'components/_basic/PasswordTextField/PasswordTextField';
import { useAuthModalsContext } from 'components/AuthModals';
import { MODAL_ERRORS } from 'components/AuthModals/types';
import { useRegistrationContext } from 'components/Registration/RegistrationContextProvider/RegistrationContextProvider';
import { ERROR_MESSAGES } from 'components/Registration/types';
import { SocialButtons } from 'components/SocialButtons/SocialButtons';

import ToastService from 'helpers/ToastService';
import { signUpPersonalInfoSchema } from 'helpers/validations/signFormsValidation';

import { EmailValidation } from './EmailValidation';
import styles from '../../Registration.module.scss';
import { EnterConfirmCodeWarning } from '../EnterConfirmCodeWarning/EnterConfirmCodeWarning';

interface IPersonalInfoStepValues {
  email: string;
  password: string;
}

export const PersonalInfoStep: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { setAuthModal } = useAuthModalsContext();
  const { nextStep, data, updateDataHandler } = useRegistrationContext();
  const { track, isPending } = usePromiseStatus();
  const { setModalError, modalError } = useAuthModalsContext();
  const { isAuthenticated } = useAuthenticated();

  const [searchParams] = useSearchParams();
  const pSignUp = searchParams.get('pSignUp');

  const location = useLocation();
  const isSubscriptionPage = location.pathname === SUBSCRIPTION_PLAN;
    const isOnlyPayedSubscriptionPage = location.pathname === SUBSCRIPTION_PLAN_2;
  const isHomePage = location.pathname === '/';

  const [emailError, setEmailError] = useState('');

  const onSubmit = async ({ email, password }: IPersonalInfoStepValues) => {
    setModalError(null);
    if(isHomePage) {
      localStorage.removeItem('choosedSubscription')
    }
    try {
      await track(
        signUp({
          username: email,
          password,
          options: {
            userAttributes: {},
            autoSignIn: true,
          },
        }),
      );
      nextStep({ email, password });
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === ERROR_MESSAGES.EMAIL_EXIST) {
          setModalError(MODAL_ERRORS.REGISTRATION_ERROR);
          updateDataHandler({ email, password });
          return;
        }
        ToastService.showError(error.message);
      }
    }
  };

  const nextStepHandler = () => {
    nextStep(data);
  };

  return (
    <>
      <Box>
        <Grid container alignItems="center" mb={{ xs: 5, sm: 3 }}>
          <Grid item xs={11}>
            <Typography className={styles.signFormTitle} variant="h1">
              {t('signUp')}
            </Typography>
          </Grid>
          {(isAuthenticated || isSubscriptionPage || (isOnlyPayedSubscriptionPage && !pSignUp)) && (
            <Grid item xs={1}>
              <CloseButton onClick={onClose} />
            </Grid>
          )}
        </Grid>
        {modalError && <EnterConfirmCodeWarning nextStepHandler={nextStepHandler} />}
        <Formik
          initialValues={{ email: data.email, password: data.password }}
          validationSchema={signUpPersonalInfoSchema}
          onSubmit={onSubmit}
        >
          {({ isValid }) => (
            <Form>
              <Field name="email" validateOnBlur>
                {({ field, meta }: FieldProps<string>) => (
                  <FormField
                    id={'personalInfo.email'}
                    label={t('Email')}
                    error={(meta.touched && meta.error) || emailError}
                    sx={{ marginBottom: '24px' }}
                  >
                    <TextField {...field} placeholder={t('enterYourEmail')} inputProps={{ maxLength: 254 }} />
                  </FormField>
                )}
              </Field>
              <Field name="password">
                {({ field, meta }: FieldProps<string>) => (
                  <FormField
                    id={'personalInfo.password'}
                    label={t('password')}
                    error={meta.touched && meta.error}
                    sx={{ marginBottom: '24px' }}
                  >
                    <PasswordTextField {...field} placeholder={t('enterYourPassword')} inputProps={{ maxLength: 16 }} />
                  </FormField>
                )}
              </Field>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                isLoading={isPending}
                disabled={Boolean(!isValid) || isPending || Boolean(emailError)}
              >
                {t('signUp')}
              </Button>
              <EmailValidation setEmailError={setEmailError} />
            </Form>
          )}
        </Formik>
        <Divider className={styles.divider}> {t('or')}</Divider>
        <SocialButtons facebookTitle={t('signUpWithFacebook')} googleTitle={t('signUpWithGoogle')} />
      </Box>
      <Box>
        <Typography variant="body2" display="inline">
          {t('alreadyHaveAnAccount')}
        </Typography>{' '}
        <Link className={styles.logInLink} href="#" onClick={() => setAuthModal(AuthModal.LOGIN)}>
          {t('logIn')}
        </Link>
        <Divider />
        <Typography display="inline" variant="body2">
          {t('singingUpAgreement')}{' '}
          <Link href="Privacy-Policy.pdf" target="_blank" rel="noopener noreferrer">
            {t('growexaPrivacyPolicy')}
          </Link>{' '}
          {t('and')}{' '}
          <Link href="Terms-of-Service.pdf" target="_blank" rel="noopener noreferrer">
            {t('termsAndConditions')}
          </Link>
        </Typography>
      </Box>
    </>
  );
};
